<template>
  <FeatureAnnouncementModal
    :is-open="isOpen"
    :announcements="cards"
    @card-displayed="handleCardDisplayed"
    @dismiss="tutorialsPanelStore.closeStaticTutorial()"
  />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { onboardingWalkthroughs } from '@/constants/onboardingChecklist';
import { useTutorialsPanelStore } from '@/stores/useTutorialsPanelStore';
import { useGetStartedStore } from '@/stores/useGetStartedStore';

/**
 * The Static Tutorial Modal (see docs/contexts/feature-announcements/CONTEXT.md): a
 * checklist topic authored as illustrated slides rather than a Guided Walkthrough. Drives
 * the Feature Announcement modal shell, which is presentational and store-agnostic, with
 * one card per slide — no seen-tracking, no expiry windows, no CTA.
 */
const tutorialsPanelStore = useTutorialsPanelStore();
const { activeStaticTutorialId } = storeToRefs(tutorialsPanelStore);

const tutorial = computed(() =>
  onboardingWalkthroughs.find((candidate) => candidate.id === activeStaticTutorialId.value),
);

const cards = computed(() =>
  (tutorial.value?.slides ?? []).map((slide, index) => ({
    id: `${tutorial.value.id}-slide-${index}`,
    name: tutorial.value.name,
    description: slide.caption,
    image: slide.image,
    imageAlt: slide?.alt || '',
    showCta: false,
  })),
);

// A topic whose slides aren't authored yet never opens, matching `useOnboardingTopics`'
// availability rule — so a stray id can't surface an empty modal.
const isOpen = computed(() => cards.value.length > 0);

/**
 * Credits the checklist the moment the last slide is viewed — the same "view, not an
 * explicit close" rule a Guided Walkthrough's `onFinalStepViewed` applies. A single-slide
 * tutorial's only card is also its last, so it's credited on open, matching how
 * `ModalContainer.vue` used to treat a single-card static-modal announcement.
 */
const handleCardDisplayed = (cardId) => {
  const lastCard = cards.value[cards.value.length - 1];
  if (lastCard && cardId === lastCard.id) {
    useGetStartedStore().markItemComplete(tutorial.value.id);
  }
};

/**
 * Fetches and decodes every slide's image up front, holding each one for as long as the modal
 * is open. The references matter: an unreferenced Image is collected and its decoded copy
 * evicted, so the slide that renders it would revalidate and decode again and visibly pop in.
 */
// shallowRef, not ref: these are DOM objects, and there's no reason to proxy them deeply.
const preloaded = shallowRef([]);

const warmImage = (src) => {
  const image = new Image();
  image.src = src;
  Promise.resolve(image.decode?.()).catch(() => {});
  return image;
};

watch(isOpen, (open) => {
  preloaded.value = open
    ? cards.value.filter(({ image }) => image).map(({ image }) => warmImage(image))
    : [];
});
</script>
